import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CheckboxComponent as CheckboxComponent_1 , CheckboxComponent } from "@components/checkbox/checkbox.component";
import { LabelComponent } from "@components/label/label.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TranslatePipe } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { map, takeUntil, tap } from "rxjs/operators";

import { SkogsvardAtgardsTyp, SkogsvardMeddelasMarkagare, VirkeskopBlobStorageTyp } from "~app/common/models/constants";
import { RuleErrorContext, RuleSeverityLevel, RuleValidationMessage, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ILeverantor } from "~app/features/virkeskop/virkesleverantor/models/virkesleverantorModel";
import { canEdit } from "~app/role-permissions";
import { KontraktDokumentComponent } from "../../kontrakt/kontrakt-dokument/kontrakt-dokument.component";
import { Atgard } from "../../models/atgard-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { AvtalMediatorService } from "../../services/avtal-mediator.service";
import { KontaktpersonMediatorService } from "../../services/kontaktperson-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { VirkeskopValidationMessageComponent } from "../../validation/validation-message/virkeskop-validation-message.component";
@Component({
    selector: "rask-skogsvard-ovrigt",
    templateUrl: "./skogsvard-ovrigt.component.html",
    styleUrls: ["./skogsvard-ovrigt.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        VirkeskopValidationMessageComponent,
        LabelComponent,
        CheckboxComponent_1,
        TextareaComponent,
        FormsModule,
        KontraktDokumentComponent,
        TranslatePipe,
        RadiobuttonGroupComponent
    ]
})

export class SkogsvardOvrigtComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild("slutrapporteradEpost") slutrapporteradEpost: CheckboxComponent;
    @ViewChild("slutrapporteradSkickaInte") slutrapporteradSkickaInte: CheckboxComponent;
    @ViewChild("slutrapporteradSms") slutrapporteradSms: CheckboxComponent;

    protected blobStorageDokumentTypSkogsvardBilaga = VirkeskopBlobStorageTyp.SkogsvardBilaga;
    protected kontraktReadonly = false;
    protected leverantorer: ILeverantor[] = [];
    protected skogsvardsAtgard: Atgard;

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly cd = inject(ChangeDetectorRef);
    private readonly kontaktpersonMediatorService = inject(KontaktpersonMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly validationService = inject(VirkeskopValidationService);

    private isLoading = true;
    private kontaktperson: Kontaktperson;

    private readonly canEdit = canEdit();

    constructor() {
        this.skogsvardsAtgard = new Atgard();
    }

    ngAfterViewInit(): void {
        window.requestAnimationFrame(() => {
            this.isLoading = false;
        });

        this.initSlutrapporteradCheckboxes();

        // För att lösa ExpressionChangedAfterItHasBeenCheckedError
        this.cd.detectChanges();
    }

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        this.atgardMediatorService.atgard$
            .subscribe(atgard => {
                this.skogsvardsAtgard = atgard;
                this.validate(false);
            });

        this.kontraktMediatorService.isReadonly$
            .pipe(
                map(readonly => readonly || !this.canEdit()),
                takeUntil(this.ngDestroyed$))
            .subscribe(readonly => this.kontraktReadonly = readonly);

        this.kontaktpersonMediatorService.kontaktperson$.pipe(
            takeUntil(this.ngDestroyed$),
            tap(kontaktperson => this.kontaktperson = kontaktperson),
            tap(() => this.validate(false))
        ).subscribe();

        this.validate(true);

        const validator = () => this.validate(false);
        this.avtalMediatorService.registerOnSaveCallback(validator);
    }

    protected getKontraktId(): number {
        return this.kontraktMediatorService.kontrakt.id;
    }

    protected initSlutrapporteradCheckboxes() {
        if (this.skogsvardsAtgard.slutrapportMeddelasMarkagare === SkogsvardMeddelasMarkagare.epost) {
            this.slutrapporteradEpost.value = true;
        }

        if (this.skogsvardsAtgard.slutrapportMeddelasMarkagare === SkogsvardMeddelasMarkagare.sms) {
            this.slutrapporteradSms.value = true;
        }

        if (this.skogsvardsAtgard.slutrapportMeddelasMarkagare === SkogsvardMeddelasMarkagare.epostOchSms) {
            this.slutrapporteradEpost.value = true;
            this.slutrapporteradSms.value = true;
        }

        if (this.skogsvardsAtgard.slutrapportMeddelasMarkagare === SkogsvardMeddelasMarkagare.skickaInte) {
            this.slutrapporteradSkickaInte.value = true;
        }
    }

    protected isSkogsvardSiktrojningOnRundvirke() {
        const isRundvirke = this.kontraktMediatorService.kontrakt.isRundvirke;
        return this.skogsvardsAtgard.atgardstyp === SkogsvardAtgardsTyp.siktrojning && isRundvirke;
    }

    protected onOvrigtChange() {
        if (!this.isLoading) {
            this.atgardMediatorService.update(this.skogsvardsAtgard);
        }
    }

    protected onSlutrapporteradChange() {
        if (this.slutrapporteradEpost.value && !this.slutrapporteradSms.value && !this.slutrapporteradSkickaInte.value) {
            this.skogsvardsAtgard.slutrapportMeddelasMarkagare = SkogsvardMeddelasMarkagare.epost;
        } else if (this.slutrapporteradSms.value && !this.slutrapporteradEpost.value && !this.slutrapporteradSkickaInte.value) {
            this.skogsvardsAtgard.slutrapportMeddelasMarkagare = SkogsvardMeddelasMarkagare.sms;
        } else if (this.slutrapporteradEpost.value && this.slutrapporteradSms.value && !this.slutrapporteradSkickaInte.value) {
            this.skogsvardsAtgard.slutrapportMeddelasMarkagare = SkogsvardMeddelasMarkagare.epostOchSms;
        } else if (!this.slutrapporteradEpost.value && !this.slutrapporteradSms.value && this.slutrapporteradSkickaInte.value) {
            this.skogsvardsAtgard.slutrapportMeddelasMarkagare = SkogsvardMeddelasMarkagare.skickaInte;
        } else {
            this.skogsvardsAtgard.slutrapportMeddelasMarkagare = null;
        }

        this.onOvrigtChange();
        this.validate(false);
    }

    protected onIndirektKostnadChange() {
        this.onOvrigtChange();
        this.validate(false);
    }

    private addMessage(messageId: string, messageSeverity: RuleSeverityLevel, message: string, messages: RuleValidationMessage[]): void {
        const kontraktGuid = this.kontraktMediatorService.kontrakt.guid;
        const validationMessage = {
            entityGuids: [
                kontraktGuid,
                this.skogsvardsAtgard.guid
            ],
            entityType: "skogsvardsatgardovrigt",
            message: message,
            messageId: messageId,
            severity: messageSeverity,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save
        } as RuleValidationMessage;

        messages.push(validationMessage);
    }

    private checkAddEpostMessage(kontaktpersonEpost: string, messages: RuleValidationMessage[]): void {
        if ((this.skogsvardsAtgard.slutrapportMeddelasMarkagare === SkogsvardMeddelasMarkagare.epost ||
            this.skogsvardsAtgard.slutrapportMeddelasMarkagare === SkogsvardMeddelasMarkagare.epostOchSms) &&
                this.isNullOrEmpty(kontaktpersonEpost)) {
            this.addMessage("slutrapportMeddelasMarkagareEpost", RuleSeverityLevel.warning, "E-postadress för kontaktperson saknas.", messages);
        }
    }

    private checkAddSmsMessage(kontaktpersonSms: string, messages: RuleValidationMessage[]): void {
        if ((this.skogsvardsAtgard.slutrapportMeddelasMarkagare === SkogsvardMeddelasMarkagare.sms ||
                        this.skogsvardsAtgard.slutrapportMeddelasMarkagare === SkogsvardMeddelasMarkagare.epostOchSms) &&
                            this.isNullOrEmpty(kontaktpersonSms)) {
            this.addMessage("slutrapportMeddelasMarkagareSms", RuleSeverityLevel.warning, "Telefonnummer för kontaktperson saknas.", messages);
        }
    }

    private isNullOrEmpty(variable: any): boolean {
        return ObjectUtils.isNullOrUndefined(variable) || variable === "";
    }

    private validate(isInit = false): void {
        this.validationService.clearMessagesByEntity(
            "skogsvardsatgardovrigt",
            [
                this.kontraktMediatorService.kontrakt.guid,
                this.skogsvardsAtgard.guid
            ]);

        const messages: RuleValidationMessage[] = [];

        if(this.skogsvardsAtgard.isReadonly) {
            return;
        }

        if(!isInit && !this.isLoading) {
            const kontaktpersonEpost = this.kontaktperson?.epost;
            const kontaktpersonSms =  this.kontaktperson?.mobil ?? this.kontaktperson?.telefon;

            if(this.isNullOrEmpty(this.skogsvardsAtgard.slutrapportMeddelasMarkagare) && !this.slutrapporteradSkickaInte.value) {
                this.addMessage("skogsvardsatgardSlutrapportMeddelasMarkagare", RuleSeverityLevel.warning,
                    "Välj minst ett sätt att meddela slutrapport till kontaktpersonen.", messages);
            } else if (this.slutrapporteradSkickaInte.value && (this.slutrapporteradEpost.value || this.slutrapporteradSms.value )) {
                this.addMessage("skogsvardsatgardSlutrapportMeddelasMarkagare", RuleSeverityLevel.error,
                    "Skicka inte information får inte väljas i kombination med andra val.", messages);
            } else if(this.skogsvardsAtgard.slutrapportMeddelasMarkagare !== SkogsvardMeddelasMarkagare.skickaInte) {
                this.checkAddEpostMessage(kontaktpersonEpost, messages);
                this.checkAddSmsMessage(kontaktpersonSms, messages);
            }
            this.validateSiktrojningIndirektKostnad(messages);
        }

        messages.forEach(message => this.validationService.updateClientSideValidation(true, message));
    }

    private validateSiktrojningIndirektKostnad(messages: RuleValidationMessage[]): void {
        if (!(this.isSkogsvardSiktrojningOnRundvirke())) {
            return;
        }

        if (this.skogsvardsAtgard.indirektKostnad === null) {
            this.addMessage("skogsvardsatgardovrigt", RuleSeverityLevel.warning,
                "När en siktröjning ingår i ett rundvirkeskontrakt behöver du ange om den ska ingå i avverkningskostnaden eller ej.",
                messages);
        }
    }
}

type Kontaktperson = {
    epost: string;
    mobil: string;
    telefon: string;
};
